// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Card,
  Column,
  H1,
  Icon,
  Illustration,
  LgPadding,
  MdSpacing,
  NavBarLogo,
  PresentationListItem,
  XsSpacing,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useCallback, useEffect } from 'react';
import rx from '~shared/assets/images/RX.png';
import progynyLogo from '~shared/assets/images/progyny_by_alto_logo.png';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getProgynyWizardFirstFill } from '~shared/selectors/checkout/progyny';
import { useSelectorShared } from '~shared/store';
import { getProgynyWizardNextRoute } from '../helpers/getProgynyWizardNextRoute';
import { ProgynyWizardPage } from './ProgynyWizardPage';

export const ProgynyWizardIntro3 = () => {
  const { getCurrentRouteName, goBack, navigate } = useNavigation();
  const { trackPageView } = useAnalytics();
  const firstFill = useSelectorShared(getProgynyWizardFirstFill);
  const isRefill = !firstFill;

  const onNext = useCallback(() => {
    const currentRoute = getCurrentRouteName();
    const nextRouteName = getProgynyWizardNextRoute(currentRoute, firstFill);
    if (!nextRouteName) return;
    navigate(nextRouteName);
  }, [firstFill, getCurrentRouteName, navigate]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.PROGYNY_INTRO_3_VIEWED,
      params: {
        is_refill: isRefill,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProgynyWizardPage
      dismissIcon="chevronleft"
      onDismiss={goBack}
      NavBarCenterContent={<NavBarLogo source={progynyLogo} />}
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.PINK_LIGHTEST}
      HeaderContent={
        <LgPadding>
          <Column center>
            <Illustration
              objectFit="contain"
              resizeMode="contain"
              size="lg"
              source={rx}
            />
            <XsSpacing />
            <H1
              testID="e2e-progyny-step3-header"
              center
            >
              What you'll need to complete your order
            </H1>
          </Column>
        </LgPadding>
      }
      buttons={[
        <Button
          key="Continue"
          onPress={onNext}
          testID="e2e-progyny-step3-button"
        >
          Get started now
        </Button>,
      ]}
    >
      <Card>
        <MdSpacing />
        <PresentationListItem
          LeftContent={<Icon name="checkcircle" />}
          text="Your treatment calendar and prescriptions, including current dosage information"
        />
        <PresentationListItem
          LeftContent={<Icon name="checkcircle" />}
          text="The amount of each medication you have on hand (if you have any from a previous cycle)"
        />
        <LgPadding topPadding={SPACING.STATIC.SM}>
          <Body>
            It should take about <Body fontFamily="semibold">5-10 minutes</Body> to get your order in. Alto will be with
            you through every step.
          </Body>
        </LgPadding>
      </Card>
    </ProgynyWizardPage>
  );
};
